import React, { useState, useEffect } from 'react';
import { MODULE_DISPLAY_NAMES } from '../../../../services/noteTypeService';
import styles from './NoteTypes.module.css';

const NoteTypeForm = ({ noteType, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState({
        noteTypeName: '',
        noteTypeDesc: '',
        visibleClientServices: false,
        visibleBilling: false,
        visibleSampleControl: false,
        visibleLaboratory: false,
        visibleQA: false,
        visibleClient: false
    });

    const [errors, setErrors] = useState({});

    // Initialize form with existing data when editing
    useEffect(() => {
        if (noteType) {
            setFormData({
                noteTypeName: noteType.noteTypeName || '',
                noteTypeDesc: noteType.noteTypeDesc || '',
                visibleClientServices: noteType.visibleClientServices || false,
                visibleBilling: noteType.visibleBilling || false,
                visibleSampleControl: noteType.visibleSampleControl || false,
                visibleLaboratory: noteType.visibleLaboratory || false,
                visibleQA: noteType.visibleQA || false,
                visibleClient: noteType.visibleClient || false
            });
        }
    }, [noteType]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.noteTypeName.trim()) {
            newErrors.noteTypeName = 'Note type name is required';
        } else if (formData.noteTypeName.length > 50) {
            newErrors.noteTypeName = 'Note type name must be 50 characters or less';
        }

        if (formData.noteTypeDesc && formData.noteTypeDesc.length > 500) {
            newErrors.noteTypeDesc = 'Description must be 500 characters or less';
        }

        // At least one module must be selected
        const hasAtLeastOneModule = 
            formData.visibleClientServices ||
            formData.visibleBilling ||
            formData.visibleSampleControl ||
            formData.visibleLaboratory ||
            formData.visibleQA ||
            formData.visibleClient;

        if (!hasAtLeastOneModule) {
            newErrors.modules = 'At least one module must be selected';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formHeader}>
                <h3>{noteType ? 'Edit Note Type' : 'Add New Note Type'}</h3>
            </div>

            <div className={styles.formBody}>
                {/* Note Type Name */}
                <div className={styles.field}>
                    <label htmlFor="noteTypeName" className={styles.label}>
                        Note Type Name *
                    </label>
                    <input
                        id="noteTypeName"
                        name="noteTypeName"
                        type="text"
                        value={formData.noteTypeName}
                        onChange={handleChange}
                        className={`${styles.input} ${errors.noteTypeName ? styles.inputError : ''}`}
                        placeholder="Enter note type name"
                        maxLength={50}
                        disabled={loading}
                    />
                    {errors.noteTypeName && (
                        <span className={styles.errorText}>{errors.noteTypeName}</span>
                    )}
                    <small className={styles.hint}>
                        {formData.noteTypeName.length}/50 characters
                    </small>
                </div>

                {/* Description */}
                <div className={styles.field}>
                    <label htmlFor="noteTypeDesc" className={styles.label}>
                        Description
                    </label>
                    <textarea
                        id="noteTypeDesc"
                        name="noteTypeDesc"
                        value={formData.noteTypeDesc}
                        onChange={handleChange}
                        className={`${styles.textarea} ${errors.noteTypeDesc ? styles.inputError : ''}`}
                        placeholder="Enter description (optional)"
                        rows={4}
                        maxLength={4000}
                        disabled={loading}
                    />
                    {errors.noteTypeDesc && (
                        <span className={styles.errorText}>{errors.noteTypeDesc}</span>
                    )}
                    <small className={styles.hint}>
                        {formData.noteTypeDesc.length}/500 characters
                    </small>
                </div>

                {/* Note Visibility */}
                <div className={styles.field}>
                    <label className={styles.label}>Module Visibility *</label>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                name="visibleClientServices"
                                checked={formData.visibleClientServices}
                                onChange={handleChange}
                                className={styles.checkbox}
                                disabled={loading}
                            />
                            <span className={styles.checkboxLabel}>{MODULE_DISPLAY_NAMES.ClientServices}</span>
                        </label>
                        <label className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                name="visibleBilling"
                                checked={formData.visibleBilling}
                                onChange={handleChange}
                                className={styles.checkbox}
                                disabled={loading}
                            />
                            <span className={styles.checkboxLabel}>{MODULE_DISPLAY_NAMES.Billing}</span>
                        </label>
                        <label className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                name="visibleSampleControl"
                                checked={formData.visibleSampleControl}
                                onChange={handleChange}
                                className={styles.checkbox}
                                disabled={loading}
                            />
                            <span className={styles.checkboxLabel}>{MODULE_DISPLAY_NAMES.SampleControl}</span>
                        </label>
                        <label className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                name="visibleLaboratory"
                                checked={formData.visibleLaboratory}
                                onChange={handleChange}
                                className={styles.checkbox}
                                disabled={loading}
                            />
                            <span className={styles.checkboxLabel}>{MODULE_DISPLAY_NAMES.Laboratory}</span>
                        </label>
                        <label className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                name="visibleQA"
                                checked={formData.visibleQA}
                                onChange={handleChange}
                                className={styles.checkbox}
                                disabled={loading}
                            />
                            <span className={styles.checkboxLabel}>{MODULE_DISPLAY_NAMES.QA}</span>
                        </label>
                        <label className={styles.checkboxItem}>
                            <input
                                type="checkbox"
                                name="visibleClient"
                                checked={formData.visibleClient}
                                onChange={handleChange}
                                className={styles.checkbox}
                                disabled={loading}
                            />
                            <span className={styles.checkboxLabel}>{MODULE_DISPLAY_NAMES.Client}</span>
                        </label>
                    </div>
                    {errors.modules && (
                        <span className={styles.errorText}>{errors.modules}</span>
                    )}
                    <small className={styles.hint}>
                        Select at least one module where this note type will be visible
                    </small>
                </div>
            </div>

            <div className={styles.formFooter}>
                <button
                    type="button"
                    onClick={onCancel}
                    className={styles.cancelButton}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (noteType ? 'Update' : 'Create')}
                </button>
            </div>
        </form>
    );
};

export default NoteTypeForm;
